import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import {
  Alert,
  Button,
  Field,
  HeaderSection,
  List,
  ListItem,
  Radio,
  Select,
  Text,
} from '@groupelacentrale/contact-storybook';
import { FormikErrors, FormikTouched, FormikValues } from 'formik';
import { zipCodePattern } from 'components/ContactInformationForm/validationSchema';
import { ContactExampleModal } from 'components/ContactInformationForm/ContactExampleModal';
import { GeolocService } from 'api';
import './ContactInformationForm.scss';
import { keypressCallback } from 'utils';

interface Props {
  errorMessage?: string;
  errors: FormikErrors<FormikValues>;
  handleTowns: (latitude: number, longitude: number, towns: string[]) => void | undefined;
  isSubmitting: boolean;
  onChange: (e: React.ChangeEvent) => void | undefined;
  onReturn: () => void | undefined;
  onSubmit: () => void | undefined;
  setFieldError: (field: string, message: string) => void;
  setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  showActions?: boolean;
  touched: FormikTouched<FormikValues>;
  updatedFields?: {
    address?: boolean;
    civility?: boolean;
    city?: boolean;
    country?: boolean;
    firstName?: boolean;
    lastName?: boolean;
    phone?: boolean;
    zipCode?: boolean;
  };
  values: {
    address: string;
    civility?: string;
    city: string;
    country: string;
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    zipCode: string;
    geoLoc?: {
      latitude: number;
      longitude: number;
    };
  };
}

const COUNTRIES = [
  { value: 'FR', label: 'France' },
  { value: 'MC', label: 'Monaco' },
];

export const ContactInformationFormView: React.FC<Props> = ({
  errorMessage,
  errors,
  isSubmitting,
  onChange,
  onReturn,
  onSubmit,
  touched,
  values,
  setFieldValue,
  setFieldError,
  setFieldTouched,
  updatedFields,
  handleTowns,
  showActions,
}) => {
  const [towns, setTowns] = useState<string[]>([]);

  useEffect(() => {
    if (String(values.zipCode).match(zipCodePattern)) {
      GeolocService.getTownsFromZipCode(values.zipCode)
        .then(r => {
          handleTowns(Number(r.lat), Number(r.lon), r.towns);
          setTowns(r.towns);
        })
        .catch(r => {
          setFieldTouched('zipCode', true, false);
          setFieldError('zipCode', r.message);
        });
    } else {
      setFieldValue('geoLoc', undefined, false);
      setFieldValue('city', '', false);
      setTowns([]);
    }
  }, [setFieldValue, setFieldError, setFieldTouched, handleTowns, values.zipCode]);

  const handleKeyPress = keypressCallback((value: string) => setFieldValue('civility', value, false), 'data-value');
  return (
    <>
      {showActions !== false && <HeaderSection noUnderline>Vos coordonnées de contact</HeaderSection>}
      <div className="fields">
        <Text>
          Vos données personnelles ne seront pas entièrement affichées sur votre annonce (nom, email, adresse et
          téléphone).
          <br />
          <ContactExampleModal />
        </Text>
        <div>
          <Text className={cx('field-label', { 'updated-field': updatedFields?.civility })}>Civilité</Text>
          <List>
            <ListItem tabIndex={0} data-value={'M.'} onKeyPress={handleKeyPress}>
              <Radio
                name="civility"
                theme="primary"
                value="M."
                label="M."
                onChange={onChange}
                checked={values.civility === 'M.'}
              />
            </ListItem>
            <ListItem tabIndex={0} data-value={'Mme'} onKeyPress={handleKeyPress}>
              <Radio
                name="civility"
                theme="primary"
                value="Mme"
                label="Mme"
                onChange={onChange}
                checked={values.civility === 'Mme'}
              />
            </ListItem>
          </List>
        </div>
        <Field
          name="lastName"
          label="Nom*"
          value={values.lastName}
          onChange={onChange}
          errorMessage={touched.lastName ? errors.lastName : undefined}
          className={updatedFields?.lastName ? 'updated-field' : undefined}
        />
        <Field
          name="firstName"
          label="Prénom*"
          value={values.firstName}
          onChange={onChange}
          errorMessage={touched.firstName ? errors.firstName : undefined}
          className={updatedFields?.firstName ? 'updated-field' : undefined}
        />
        <Field
          name="address"
          label="Adresse*"
          value={values.address}
          onChange={onChange}
          errorMessage={touched.address ? errors.address : undefined}
          className={updatedFields?.address ? 'updated-field' : undefined}
        />
        <Field
          name="zipCode"
          label="Code postal*"
          value={values.zipCode}
          onChange={onChange}
          errorMessage={touched.zipCode ? errors.zipCode : undefined}
          className={updatedFields?.zipCode ? 'updated-field' : undefined}
        />
        {towns.length <= 1 && (
          <Field
            name="city"
            label="Ville*"
            disabled
            value={values.city}
            onChange={onChange}
            errorMessage={touched.city ? errors.city : undefined}
            className={updatedFields?.city ? 'updated-field' : undefined}
          />
        )}
        {towns.length > 1 && (
          <div className="field-wrapper">
            <Text className={cx('field-label', { 'updated-field': updatedFields?.city })}>Ville*</Text>
            <Select
              name="city"
              onChange={onChange}
              errorMessage={touched.city ? errors.city : undefined}
              value={values.city}
              options={towns.map(t => ({
                label: t,
                value: t,
              }))}
            />
          </div>
        )}
        <div className="field-wrapper">
          <Text className={cx('field-label', { 'updated-field': updatedFields?.country })}>Pays*</Text>
          <Select
            name="country"
            onChange={onChange}
            errorMessage={touched.country ? errors.country : undefined}
            value={values.country}
            options={COUNTRIES.map(c => ({
              label: c.label,
              value: c.value,
            }))}
          />
        </div>
        <Field label="Email*" disabled value={values.email} />
        <Field
          name="phone"
          label="Téléphone*"
          value={values.phone}
          onChange={onChange}
          errorMessage={touched.phone ? errors.phone : undefined}
          className={updatedFields?.phone ? 'updated-field' : undefined}
        />
        {errorMessage && <Alert type="error">{errorMessage}</Alert>}
        <Text>* champ obligatoire</Text>
      </div>

      {showActions !== false && (
        <div className="actions">
          <Button label="Retour" theme="quaternary" type="button" onClick={onReturn} />
          <Button
            label="Valider et continuer"
            disabled={
              !(
                values.email &&
                values.phone &&
                values.country &&
                values.city &&
                values.zipCode &&
                values.lastName &&
                values.firstName &&
                values.address
              ) || isSubmitting
            }
            loading={isSubmitting}
            type="submit"
            onClick={onSubmit}
          />
        </div>
      )}

      <div className="legal-infos">
        <Text size="small">
          Les données que vous renseignez dans ce formulaire sont traitées par le Groupe La Centrale en qualité de
          responsable de traitement.
          <br />
          Les données obligatoires sont celles signalées par un astérisque dans ce formulaire.
          <br />
          Ces données sont utilisées à des fins de :
        </Text>
        <ul>
          <li>
            <Text size="small">
              fourniture des services qui vous sont proposés, en tant qu’annonceur, sur La Centrale (et notamment de
              publication d’une annonce, de mise en relation avec des acheteurs, etc.) ;
            </Text>
          </li>
          <li>
            <Text size="small">gestion de votre relation commerciale avec le Groupe La Centrale ;</Text>
          </li>
          <li>
            <Text size="small">mesure de l’audience de la plateforme ;</Text>
          </li>
          <li>
            <Text size="small">lutte anti-fraude.</Text>
          </li>
        </ul>
        <Text size="small">
          Vous disposez d’un droit d’accès, de rectification, d’effacement de ces données, d’un droit de limitation du
          traitement, d’un droit d’opposition, du droit à la portabilité de vos données et du droit d’introduire une
          réclamation auprès d’une autorité de contrôle (en France, la CNIL).
          <br />
          Pour en savoir plus sur le traitement de vos données : &nbsp;
          <a className="force-word-break" href="https://www.lacentrale.fr/politiqueconfidentialite.php">
            https://www.lacentrale.fr/politiqueconfidentialite.php
          </a>
        </Text>
      </div>
    </>
  );
};
